import React, { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import API from "../api/apiClient";
import html2canvas from "html2canvas";
import jsPDF from "jspdf";

export default function ResultPage() {
  const page1 = useRef(null);
  const page2 = useRef(null);
  const { state } = useLocation();

  const [category, setCategory] = useState("");
  const [enterprise, setEnterprise] = useState("");
  const [incentives, setIncentives] = useState([]);

  const details = state?.details || {};
  const costs = state?.costs || {};
  const inputs = state?.inputs || {};

  // FORMATTER — Always show 7 digits after decimal
  const format = (n) => Number(n || 0).toFixed(3);

  // Fixed capital investment
  const FCI =
    (parseFloat(costs.land) || 0) +
    (parseFloat(costs.building) || 0) +
    (parseFloat(costs.machinery) || 0) +
    (parseFloat(costs.other_assets) || 0) +
    (parseFloat(costs.contingency) || 0) +
    (parseFloat(costs.pre_op) || 0);

  // Stamp duty
  const stampDutyCost = (parseFloat(costs.land) || 0) * 0.07;

  // Block category
  useEffect(() => {
    if (!details.block) return;

    API.get("/location/category", { params: { block: details.block } })
      .then((r) => setCategory(r.data.category || "-"))
      .catch(() => setCategory("-"));
  }, [details.block]);

  // Enterprise type
  useEffect(() => {
    const P = parseFloat(costs.machinery) || 0;
    const T = parseFloat(details.turnover) || 0;

    if (FCI >= 1500) setEnterprise("Ultra-Mega");
    else if (FCI >= 200) setEnterprise("Mega");
    else if (P > 125 || T > 500) setEnterprise("Large");
    else if ((P > 26 && P <= 125) || (T > 101 && T <= 500))
      setEnterprise("Medium");
    else if ((P > 2.6 && P <= 25) || (T > 11 && T <= 100))
      setEnterprise("Small");
    else if ((P > 0.1 && P <= 2.5) || (T > 0.1 && T <= 10))
      setEnterprise("Micro");
    else setEnterprise("-");
  }, [FCI, costs.machinery, details.turnover]);

  // Sector logic
  const finalSector =
    details.sector === "Based on Data"
      ? enterprise === "Ultra-Mega"
        ? "Ultra-Mega Project"
        : enterprise === "Mega"
          ? "Mega Project"
          : enterprise === "Large"
            ? "Large Enterprise"
            : enterprise === "Medium"
              ? "Medium Enterprise"
              : enterprise === "Small"
                ? "Small Enterprise"
                : enterprise === "Micro"
                  ? "Micro Enterprise"
                  : "-"
      : details.sector;

  // FETCH INCENTIVES
  useEffect(() => {
    if (!category || !enterprise || !finalSector) return;

    API.get("/incentives", {
      params: { sector: finalSector, enterprise, block: category },
    })
      .then((res) => setIncentives(res.data || []))
      .catch(() => setIncentives([]));
  }, [category, enterprise, finalSector]);

  // Input values
  const edcCost = Number(inputs.edc || 0);
  const elecCost = Number(inputs.electricityDuty || 0);
  const dgCost = Number(inputs.dgSet || 0);
  const units = Number(inputs.powerUnit || 0);
  const unitCost = Number(inputs.perUnitCost || 0);
  const totalPowerCost = units * unitCost * (10 ** -7);

  const totalSales = [...Array(3).keys()].reduce(
    (sum, i) => sum + Number(inputs[`sales${i + 1}`] || 0),
    0
  );
  const SGST = totalSales * 0.09;

  // Get % from DB
  const getPercent = (type) => {
    const row = incentives.find(
      (x) => x.incentive_type?.toLowerCase() === type.toLowerCase()
    );
    return row ? Number(row.refund_benefit) || 0 : 0;
  };

  const pctStamp = getPercent("Stamp Duty");
  const pctEDC = getPercent("EDC Exemption");
  const pctED = getPercent("Electricity Duty");
  const pctSGST = getPercent("Net SGST Reimbursement");
  const perUnit = getPercent("Power Tariff Subsidy");
  const pctDG = getPercent("DG Set Subsidy");

  const selectedFCI = state?.selectedFCI || FCI;

  // Refund amount 
  const refundStamp = (stampDutyCost * pctStamp) / 100;
  const refundEDC = (edcCost * pctEDC) / 100;
  const refundED = (elecCost * pctED) / 100;
  const refundSGST = (totalSales * 0.09 * pctSGST) / 100;
  const refundPower = (units * perUnit * (10 ** -7));
  const refundDG = (dgCost * pctDG) / 100;

  const totalCost =
    stampDutyCost + edcCost + elecCost + SGST + dgCost + totalPowerCost;

  const totalRefund = Math.min(
    refundStamp +
    refundEDC +
    refundED +
    refundSGST +
    refundPower +
    refundDG,

    selectedFCI
  );


  // EMAIL SEND LOGIC
  const emailSentRef = useRef(false);

  const autoSendReport = async () => {
    if (!page1.current || !page2.current) return;

    try {
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const margin = 15;
      const pageWidth =
        pdf.internal.pageSize.getWidth() - margin * 2;

      const startY = margin + 10;

      const canvasOptions = {
        scale: 1,
        useCORS: true,
        scrollY: -window.scrollY,
      };

      const canvas1 = await html2canvas(
        page1.current,
        canvasOptions
      );

      const imgData1 = canvas1.toDataURL(
        "image/jpeg",
        0.4
      );

      const imgHeight1 =
        (canvas1.height * pageWidth) / canvas1.width;

      pdf.addImage(
        imgData1,
        "JPEG",
        margin,
        startY,
        pageWidth,
        imgHeight1
      );

      pdf.addPage();

      const canvas2 = await html2canvas(
        page2.current,
        canvasOptions
      );

      const imgData2 = canvas2.toDataURL(
        "image/jpeg",
        0.4
      );

      const imgHeight2 =
        (canvas2.height * pageWidth) / canvas2.width;

      pdf.addImage(
        imgData2,
        "JPEG",
        margin,
        startY,
        pageWidth,
        imgHeight2
      );

      const pdfBlob = pdf.output("blob");

      const formData = new FormData();
      formData.append("file", pdfBlob, "Incentive_Result.pdf");

      await API.post("/send-report", formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });



    } catch (err) {
  // optional error handling
}
  };

  useEffect(() => {

    const timer = setTimeout(async () => {

      if (emailSentRef.current) {
        return;
      }

      emailSentRef.current = true;

      await autoSendReport();

    }, 1200);

    return () => clearTimeout(timer);

  }, []);

  // UI
  return (
    <div className="max-w-5xl mx-auto mt-10 bg-white shadow p-6 rounded">
      <div ref={page1}>
        <h1 className="text-2xl font-semibold mb-3">
          1. Data Summary
        </h1>

        {/* SUMMARY TABLE */}
        <table className="w-full border mb-6">
          <tbody>
            <tr><td className="border p-2">Block</td><td className="border p-2">{details.block}</td></tr>
            <tr><td className="border p-2">Village</td><td className="border p-2">{details.village}</td></tr>
            <tr><td className="border p-2">Block Category</td><td className="border p-2">{category}</td></tr>
            <tr><td className="border p-2">Sector</td><td className="border p-2">{finalSector}</td></tr>
            <tr><td className="border p-2">Enterprise</td><td className="border p-2">{enterprise}</td></tr>
            <tr><td className="border p-2">FCI</td><td className="border p-2">₹ {format(FCI)}</td></tr>
            <tr><td className="border p-2">Turnover</td><td className="border p-2">₹ {format(details.turnover)}</td></tr>
          </tbody>
        </table>

        {/* CALCULATIONS */}
        <h2 className="text-2xl font-semibold mb-3">2. Incentive Calculation</h2>

        <table className="w-full border mb-6">
          <thead>
            <tr className="bg-blue-100 font-bold">
              <th className="border p-2 text-left">Incentive Type</th>
              <th className="border p-2 text-left">Cost</th>
              <th className="border p-2 text-left">Refund Amount</th>
            </tr>
          </thead>

          <tbody>
            <tr className="bg-gray-100 font-bold">
              <td className="border p-2">Stamp Duty</td>
              <td className="border p-2">₹ {format(stampDutyCost)}</td>
              <td className="border p-2">₹ {format(refundStamp)}</td>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <td className="border p-2">EDC</td>
              <td className="border p-2">₹ {format(edcCost)}</td>
              <td className="border p-2">₹ {format(refundEDC)}</td>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <td className="border p-2">Electricity Duty</td>
              <td className="border p-2">₹ {format(elecCost)}</td>
              <td className="border p-2">₹ {format(refundED)}</td>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <td className="border p-2">SGST</td>
              <td className="border p-2">₹ {format(SGST)}</td>
              <td className="border p-2">₹ {format(refundSGST)}</td>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <td className="border p-2">Power Tariff</td>
              <td className="border p-2">₹ {format(totalPowerCost)}</td>
              <td className="border p-2">₹ {format(refundPower)}</td>
            </tr>
            <tr className="bg-gray-100 font-bold">
              <td className="border p-2">DG Set</td>
              <td className="border p-2">₹ {format(dgCost)}</td>
              <td className="border p-2">₹ {format(refundDG)}</td>
            </tr>
          </tbody>
        </table>


        {/* TOTALS */}
        <h2 className="text-2xl font-semibold mb-3">3. Totals</h2>
        <table className="w-full border mb-6">
          <tbody>
            <tr><td className="border p-2 font-bold">Total Cost</td><td className="border p-2">₹ {format(totalCost)}</td></tr>
            <tr><td className="border p-2 font-bold">Total Refund</td><td className="border p-2">₹ {format(totalRefund)}</td></tr>
          </tbody>
        </table>
      </div>

      {/* INCENTIVES */}
      <div ref={page2}>
        <h2 className="text-xl font-bold text-blue-900 mb-3">Applicable Incentives</h2>

        <table className="w-full border mb-6">
          <thead>
            <tr className="bg-gray-100">
              <th className="border p-2">Incentive Type</th>
              <th className="border p-2">Details</th>
            </tr>
          </thead>
          <tbody>
            {incentives.length === 0 ? (
              <tr>
                <td colSpan="2" className="border p-3 text-center text-gray-500">
                  No incentives available.
                </td>
              </tr>
            ) : (
              incentives.map((inc, i) => (
                <tr key={i}>
                  <td className="border p-2">{inc.incentive_type}</td>
                  <td className="border p-2">{inc.details}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

